import { Tooltip } from '@enechain/ecloud-designsystem'

type Props = {
  /**
   * hover で出す説明。 `undefined` / 空文字なら出さない
   * (= 「省略された時だけ全文を出す」 形をそのまま書ける)。
   */
  content: string | undefined
  /** 説明を付ける要素。 DS `Tooltip` の制約で **単一要素**であること。 */
  children: React.ReactElement
}

/**
 * 文字が省略された時の全文表示など、 hover の説明を DS `Tooltip` で出す
 * **器を増やさない**共通ラッパー。
 *
 * デザイナー既定 (2026-08-17): hover の説明は native `title` ではなく DS `Tooltip`
 * で出す (`title` は Tooltip 非対応環境向けの保険として残す)。
 *
 * ## [[ButtonTooltip]] との使い分け
 *
 * [[ButtonTooltip]] は **無効化されうるボタン**専用。 `disabled` なボタンは
 * pointer event を捨てるので `<span>` を 1 枚挟んで hover を拾っている。
 * その `<span>` を挟めない・挟む必要が無い要素 (表のセル、 grid の直下、
 * リンク、 常に押せるボタン、 ただの表示) はこちらを使う。 押せるかどうかで
 * 分かれるのではなく、 **器を挟めるかどうか**で分かれる。
 *
 * ## 表のセルに使ってよい理由
 *
 * DS `Tooltip` は **children を包まず、 属性だけを足す** (2026-08-20 実測:
 * `<tr>` 直下に `<td>` が残り、 `data-part="trigger"` が付くだけ)。 そのため
 * `<td>` / `<th>` に直接使っても表の構造は壊れない。
 *
 * ## 説明の有無が変わると中身は作り直される
 *
 * 出し分けは `isHidden` に任せている。 `isHidden` の切り替えは子要素を
 * unmount / remount するので (2026-08-20 実測)、 説明が出入りすると DOM ノードが
 * 変わり focus が外れる。 focus を持つ要素には**出入りしない** `content`
 * (常に非空の文字列) を渡すこと。
 */
export const HintTooltip = ({
  content,
  children,
}: Props): React.ReactElement => (
  <Tooltip
    content={content ?? ''}
    isHidden={content === undefined || content === ''}
  >
    {children}
  </Tooltip>
)
